import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { showToast } from '../context/ToastHandle';
import { getBoardUsage } from '../context/accountUtils';

const sortOptions = [
  { value: 'popular', label: 'Popular' },
  { value: 'featured', label: 'Featured' },
  { value: 'likes', label: 'Most liked' },
  { value: 'downloads', label: 'Most downloaded' },
  { value: 'az', label: 'A–Z' },
];

const TYPE_LABELS = {
  category: 'Collection', folder: 'Folder', vocabulary: 'Vocabulary', core: 'Core language',
  quick: 'Quick communication', learning: 'Learning', routine: 'Routine', health: 'Health', social: 'Social',
};

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const CATEGORY_VISUALS = {
  'Core Communication': { icon: '💬', tint: '#cae2fd' },
  'Needs & Safety': { icon: '🆘', tint: '#fce5cc' },
  'Feelings & Regulation': { icon: '💛', tint: '#fdf7dd' },
  'Health & Body': { icon: '🩺', tint: '#cdfade' },
  'Daily Routines': { icon: '🗓️', tint: '#d6dcfd' },
  'Food & Drink': { icon: '🍎', tint: '#d8bfb4' },
  'Home & Chores': { icon: '🏠', tint: '#e6c4c4' },
  'People & Relationships': { icon: '👥', tint: '#fdcfe7' },
  'School & Learning': { icon: '🎓', tint: '#a9c2d3' },
  'Play & Leisure': { icon: '🧩', tint: '#d3c9e6' },
  'Animals & Nature': { icon: '🐾', tint: '#c1e4c1' },
  'Places & Community': { icon: '📍', tint: '#948a7d83' },
  'Transport & Travel': { icon: '🚌', tint: '#abbdd6a1' },
  'Technology & Media': { icon: '📱', tint: '#afd8da' },
  'Sports & Movement': { icon: '⚽', tint: '#64af9c9f' },
  'Creativity & Music': { icon: '🎨', tint: '#be99ac' },
  'Time & Concepts': { icon: '⏰', tint: '#b282ff71' },
  'Seasonal & Events': { icon: '🎉', tint: '#eec1cf' },
  'Teen & Adult Life': { icon: '🌱', tint: '#14f5aa8c' },
  'Community & Independence': { icon: '🧭', tint: '#0a325f7c' },
};

const getVisual = (item) =>
  CATEGORY_VISUALS[item.category_name] ||
  CATEGORY_VISUALS[item.name] ||
  { icon: item.board_type === 'folder' ? '📁' : '🗂️', tint: '#abccee' };

export default function PresetBoards({ embedded = false, onImported }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState('popular');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalBoards, setTotalBoards] = useState(0);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [liking, setLiking] = useState(false);
  const [importing, setImporting] = useState(false);

  // Search as the user types, but debounce briefly so every keystroke does not
  // create a network/database request.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQ(q.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [q]);

  // A new search/filter/sort always starts from page 1.
  useEffect(() => {
    setPage(1);
  }, [debouncedQ, category, sort]);

  // Dropdown/page changes update immediately; text search updates after debounce.
  useEffect(() => {
    fetchResults();
  }, [debouncedQ, category, sort, page]);

  async function fetchResults() {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (debouncedQ) params.set('q', debouncedQ);
      if (category !== 'all') params.set('category', category);

      params.set('sort', sort);
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String((page - 1) * PAGE_SIZE));

      const { data } = await api.get(`/preset_search.php?${params.toString()}`);
      if (!data?.success) throw new Error(data?.error || 'Search failed');

      setItems(data.data || []);

      if (Array.isArray(data.categories)) {
        setCategories(data.categories);
      }

      const pagination = data.pagination || {};
      const nextTotal = Number(pagination.total || 0);
      const nextFiltered = Number(
        pagination.filtered_total ??
        pagination.total ??
        (data.data || []).length
      );
      const nextPages = Math.max(
        1,
        Number(pagination.total_pages || Math.ceil(nextFiltered / PAGE_SIZE) || 1)
      );

      setTotalBoards(nextTotal);
      setFilteredTotal(nextFiltered);
      setTotalPages(nextPages);

      // If records were removed and the current page no longer exists,
      // move back to the last valid page.
      if (page > nextPages) {
        setPage(nextPages);
      }
    } catch (e) {
      console.error(e);
      showToast('Failed to load preset boards.', 'error', false);
    } finally {
      setLoading(false);
    }
  }

  async function openPreset(item) {
    setActive(item); setOpen(true); setDetail(null); setDetailLoading(true);
    document.body.classList.add('modal-open');
    try {
      const { data } = await api.get(`/preset_detail.php?id=${item.id}`);
      if (!data?.success) throw new Error(data?.error || 'Failed');
      setDetail(data.data);
    } catch (e) {
      console.error(e); showToast('Could not load preset preview.', 'error', false);
    } finally { setDetailLoading(false); }
  }

  function closePreset() {
    setOpen(false); setActive(null); setDetail(null); document.body.classList.remove('modal-open');
  }

  async function toggleLike(id) {
    try {
      setLiking(true);
      const { data } = await api.post('/preset_like.php', { preset_board_id: id });
      if (!data?.success) throw new Error(data?.error || 'Like failed');
      const update = it => it.id === id ? { ...it, user_liked: !!data.liked, likes_count: data.likes_count } : it;
      setItems(prev => prev.map(update));
      setActive(prev => prev?.id === id ? update(prev) : prev);
      setDetail(prev => prev?.board?.id === id ? { ...prev, board: update(prev.board) } : prev);
    } catch (e) { console.error(e); showToast('Like action failed.', 'error', false); }
    finally { setLiking(false); }
  }

  async function importPreset(item) {
    try {
      setImporting(true);
      const required = Number(detail?.package?.board_count || item.package_board_count || 1);
      const usage = await getBoardUsage();
      if (usage && Number.isFinite(usage.remaining) && usage.remaining < required) {
        showToast(`This preset needs ${required} board${required === 1 ? '' : 's'}, but your plan has ${usage.remaining} remaining.`, 'error', false);
        return;
      }
      const { data } = await api.post('/preset_import.php', { preset_board_id: item.id });
      if (!data?.success) throw new Error(data?.error || 'Import failed');
      const created = Number(data.data?.created_count || 1);
      showToast(`Preset imported successfully — ${created} board${created === 1 ? '' : 's'} added.`, 'success', false);
      setItems(prev => prev.map(it => it.id === item.id ? { ...it, downloads_count: (it.downloads_count || 0) + 1 } : it));
      if (typeof onImported === 'function') onImported(data.data);
    } catch (e) {
      const resp = e?.response?.data;
      if (resp?.error === 'board_limit') {
        showToast(`Not enough board space on your plan. This preset needs ${resp.required} boards.`, 'error', false);
      } else {
        console.error(e); showToast('Preset import failed.', 'error', false);
      }
    } finally { setImporting(false); }
  }

  const modal = open && active ? createPortal(
    <div onClick={closePreset} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.62)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10000 }}>
      <div onClick={e => e.stopPropagation()} style={{ width:'min(960px,92vw)', maxHeight:'90vh', background:'#fff', borderRadius:14, overflow:'auto', boxShadow:'0 12px 36px rgba(0,0,0,.35)' }}>
        <div style={{ position:'sticky', top:0, zIndex:2, display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 18px', borderBottom:'1px solid #eee', background:'#fff' }}>
          <div><div style={{ fontWeight:800, fontSize:20 }}>{active.name}</div><div style={{ color:'#667085', fontSize:13 }}>{active.category_name} · {TYPE_LABELS[active.board_type] || active.board_type}</div></div>
          <button onClick={closePreset} style={{ border:0, background:'transparent', fontSize:28, cursor:'pointer' }}>×</button>
        </div>
        <div style={{ padding:18 }}>
          {detailLoading ? <div>Loading preview…</div> : <>
            <p style={{ lineHeight:1.6, color:'#475467' }}>{detail?.board?.description || active.description}</p>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))', gap:10, margin:'16px 0' }}>
              {(detail?.tiles || []).filter(t => t.nav_role === 'none').map(t => (
                <div key={t.id} style={{ minHeight:88, padding:10, border:'1px solid #d0d5dd', borderRadius:10, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:t.color || '#f9fafb', textAlign:'center', fontWeight:600 }}>
                  {t.image_url ? <img src={t.image_url} alt="" style={{ width:42, height:42, objectFit:'contain', marginBottom:5 }} /> : null}
                  {t.label}
                </div>
              ))}
            </div>
            <div style={{ background:'#f8fafc', border:'1px solid #e4e7ec', borderRadius:10, padding:12, marginBottom:14 }}>
              <strong>Import package:</strong> {detail?.package?.board_count || active.package_board_count} linked board{(detail?.package?.board_count || active.package_board_count) === 1 ? '' : 's'}
              <div style={{ marginTop:5, fontSize:13, color:'#667085' }}>{(detail?.package?.boards || []).map(b => b.name).join(' · ')}</div>
            </div>
          </>}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <div style={{ color:'#475467' }}>👍 {active.likes_count || 0} likes &nbsp; ⬇️ {active.downloads_count || 0} imports</div>
            <div style={{ display:'flex', gap:8 }}>
              <button disabled={liking} onClick={() => toggleLike(active.id)} style={{ padding:'9px 13px', borderRadius:8, border:'1px solid #d0d5dd', background:active.user_liked ? '#eaf7ec' : '#fff', color:active.user_liked ? '#2e7d32' : '#344054', cursor:'pointer', fontWeight:600 }}>{active.user_liked ? '💚 Liked' : '👍 Like'}</button>
              <button disabled={importing || detailLoading} onClick={() => importPreset(active)} style={{ padding:'9px 15px', borderRadius:8, border:0, background:'#2e7d32', color:'#fff', cursor:'pointer', fontWeight:700 }}>{importing ? 'Importing…' : '⬇️ Add to My Boards'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>, document.body) : null;

  return <div
    style={{
      padding: embedded ? 0 : '18px clamp(14px, 2vw, 28px)',
      maxWidth: embedded ? 'none' : 1500,
      margin: embedded ? 0 : '0 auto',
      boxSizing: 'border-box',
    }}
  >
    {!embedded && (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 14,
          marginBottom: 18,
        }}
      >
        <div>
          <h2 style={{ margin: '0 0 4px', fontSize: 28, color: '#101828' }}>
            Preset Boards
          </h2>
          <div style={{ color: '#667085', fontSize: 14 }}>
            Find a ready-made board, preview it, then add it to your collection.
          </div>
          <div style={{ color: '#667085', fontSize: 10, marginTop: 5 }}>
            Please note: Preset boards were produced by the iBoard team with AI learning and may contain errors. Review each board carefully before use.
          </div>
        </div>
        <button
          onClick={() => navigate('/boards')}
          style={{
            border: '1px solid #d0d5dd',
            background: '#fff',
            borderRadius: 10,
            padding: '10px 14px',
            cursor: 'pointer',
            fontWeight: 650,
            whiteSpace: 'nowrap',
          }}
        >
          ← My Boards
        </button>
      </div>
    )}

    <div
      className="preset-search-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(260px,1fr) 210px 180px',
        gap: 9,
        marginBottom: 18,
        padding: 10,
        background: '#f8fafc',
        border: '1px solid #eef2f6',
        borderRadius: 12,
      }}
    >
      <input
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Search presets — animals, pain, school…"
        style={{
          padding: '11px 12px',
          border: '1px solid #d0d5dd',
          borderRadius: 8,
          background: '#fff',
        }}
      />
      <select
        value={category}
        onChange={e => setCategory(e.target.value)}
        style={{ padding: '10px', border: '1px solid #d0d5dd', borderRadius: 8, background: '#fff' }}
      >
        <option value="all">All categories</option>
        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <select
        value={sort}
        onChange={e => setSort(e.target.value)}
        style={{ padding: '10px', border: '1px solid #d0d5dd', borderRadius: 8, background: '#fff' }}
      >
        {sortOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
    </div>

    <div
        style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        margin: '0 2px 14px',
        color: '#667085',
        fontSize: 13,
      }}
    >
      <div>
        <strong style={{ color: '#344054' }}>
          {totalBoards.toLocaleString()} preset board{totalBoards === 1 ? '' : 's'}
        </strong>
        {(debouncedQ || category !== 'all') && (
          <span>
            {' '}· {filteredTotal.toLocaleString()} matching
          </span>
        )}
      </div>

      {filteredTotal > 0 && (
        <div>
          Showing {((page - 1) * PAGE_SIZE) + 1}–
          {Math.min(page * PAGE_SIZE, filteredTotal)} of {filteredTotal.toLocaleString()}
        </div>
      )}
    </div>

    {loading ? (
      <div style={{ padding: 28, textAlign: 'center', color: '#667085' }}>
        Loading preset boards…
            </div>
    ) : items.length === 0 ? (
      <div
        style={{
          padding: 28,
          textAlign: 'center',
          border: '1px dashed #d0d5dd',
          borderRadius: 12,
          color: '#667085',
        }}
      >
        No presets found. Try another search.
      </div>
    ) : (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(275px,1fr))',
          gap: 16,
        }}
      >
        {items.map(it => {
          const visual = getVisual(it);
          const previewLabels = (it.preview_labels || []).slice(0, 4);

          return (
            <article
              key={it.id}
              style={{
                border: '1px solid #e4e7ec',
                borderRadius: 14,
                background: '#fff',
                boxShadow: '0 3px 12px rgba(16,24,40,.06)',
                overflow: 'hidden',
                minHeight: 250,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <button
                onClick={() => openPreset(it)}
                style={{
                  width: '100%',
                  border: 0,
                  padding: 0,
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                }}
              >
                <div
                  style={{
                    minHeight: 112,
                    padding: '18px 16px 14px',
                    background: visual.tint,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                  }}
                >
                  <div
                    aria-hidden="true"
                    style={{
                      width: 58,
                      height: 58,
                      borderRadius: 14,
                      background: '#fff',
                      border: '1px solid rgba(16,24,40,.08)',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 30,
                      flex: '0 0 auto',
                      boxShadow: '0 2px 8px rgba(16,24,40,.06)',
                    }}
                  >
                    {visual.icon}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        display: 'inline-block',
                        marginBottom: 6,
                        padding: '3px 7px',
                        borderRadius: 999,
                        background: 'rgba(255,255,255,.8)',
                        color: '#475467',
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {TYPE_LABELS[it.board_type] || it.board_type}
                    </div>
                    <div
                      style={{
                        fontWeight: 800,
                        fontSize: 18,
                        lineHeight: 1.2,
                        color: '#101828',
                      }}
                    >
                      {it.name}
                    </div>
                  </div>
                </div>

                <div style={{ padding: '14px 14px 10px', flex: 1 }}>
                  {previewLabels.length > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 6,
                        marginBottom: 12,
                      }}
                    >
                      {previewLabels.map((label, i) => (
                        <span
                          key={i}
                          style={{
                            padding: '5px 8px',
                            borderRadius: 999,
                            background: '#f2f4f7',
                            color: '#344054',
                            fontSize: 11,
                            fontWeight: 650,
                          }}
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  )}

                  {it.parent_name && (
                    <div style={{ color: '#667085', fontSize: 12, marginBottom: 6 }}>
                      In {it.parent_name}
                    </div>
                  )}

                  <div
                    style={{
                      color: '#667085',
                      fontSize: 12.5,
                      lineHeight: 1.4,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {it.description}
                  </div>
                </div>
          </button>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 14px',
                  borderTop: '1px solid #f0f2f5',
                  color: '#667085',
                  fontSize: 12,
                }}
              >
                <span>👍 {it.likes_count || 0} &nbsp; ⬇️ {it.downloads_count || 0}</span>
                <span style={{ fontWeight: 650 }}>
                  {it.package_board_count} board{it.package_board_count === 1 ? '' : 's'}
                </span>
              </div>
            </article>
          );
        })}
      </div>
    )}

    {totalPages > 1 && !loading && (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          marginTop: 22,
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => {
            setPage(prev => Math.max(1, prev - 1));
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          style={{
            border: '1px solid #d0d5dd',
            background: page <= 1 ? '#f2f4f7' : '#fff',
            color: page <= 1 ? '#98a2b3' : '#344054',
            borderRadius: 8,
            padding: '8px 12px',
            cursor: page <= 1 ? 'default' : 'pointer',
            fontWeight: 650,
          }}
        >
          ← Previous
        </button>

        {Array.from({ length: totalPages }, (_, index) => index + 1)
          .filter(pageNumber =>
            pageNumber === 1 ||
            pageNumber === totalPages ||
            Math.abs(pageNumber - page) <= 2
          )
          .reduce((acc, pageNumber, index, visible) => {
            if (index > 0 && pageNumber - visible[index - 1] > 1) {
              acc.push(
                <span key={`gap-${pageNumber}`} style={{ color: '#98a2b3', padding: '0 2px' }}>
                  …
                </span>
              );
            }

            acc.push(
              <button
                key={pageNumber}
                type="button"
                onClick={() => {
                  setPage(pageNumber);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                aria-current={pageNumber === page ? 'page' : undefined}
                style={{
                  minWidth: 36,
                  height: 36,
                  border: pageNumber === page
                    ? '1px solid #101828'
                    : '1px solid #d0d5dd',
                  background: pageNumber === page ? '#101828' : '#fff',
                  color: pageNumber === page ? '#fff' : '#344054',
                  borderRadius: 8,
                  cursor: pageNumber === page ? 'default' : 'pointer',
                  fontWeight: 700,
                }}
              >
                {pageNumber}
              </button>
            );

            return acc;
          }, [])}

        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => {
            setPage(prev => Math.min(totalPages, prev + 1));
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          style={{
            border: '1px solid #d0d5dd',
            background: page >= totalPages ? '#f2f4f7' : '#fff',
            color: page >= totalPages ? '#98a2b3' : '#344054',
            borderRadius: 8,
            padding: '8px 12px',
            cursor: page >= totalPages ? 'default' : 'pointer',
            fontWeight: 650,
          }}
        >
          Next →
        </button>
      </div>
    )}

    {modal}

    <style>{`
      @media (max-width: 850px) {
        .preset-search-grid {
          grid-template-columns: 1fr !important;
        }
      }
    `}</style>
  </div>;
}
